import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import CopyLink from '../../components/CopyLink.jsx';
import { ROLES, CAPABILITIES, roleMeta } from './roles.js';
import './user-role-management.scss';

const PAGE_SIZE = 6;
const PHONE_RE = /^\+63 ?9\d{2}[ -]?\d{3}[ -]?\d{4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

function Modal({ id, title, icon, onClose, children, footer, size = 'lg' }) {
  return (
    <div className="modal d-block urm-modal-backdrop" id={id} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
      <div className={`modal-dialog modal-${size} modal-dialog-centered`}>
        <div className="modal-content shadow-lg bg-white">
          <div className="modal-header">
            <h5 className="modal-title fw-bold text-primary" id={`${id}-title`}>
              <i className={`fas ${icon} me-2`} aria-hidden="true" />{title}
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body p-4">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function UserForm({ initial, isEdit, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    email: initial?.email || '',
    role: initial?.role || 'driver',
    status: initial?.is_active === false ? 'suspended' : 'active',
    phone: initial?.phone_number || '',
    rfid: initial?.rfid_tag || '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = 'Full name is required.';
    if (!isEdit && !EMAIL_RE.test(form.email.trim())) next.email = 'A valid email is required.';
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) next.phone = 'Use a valid PH mobile number (+63 9XX XXX XXXX).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    setServerError('');
    try {
      await onSubmit(form);
    } catch (err) {
      setServerError(err.message || 'Could not save the user.');
      setSaving(false);
    }
  };

  const cls = (k) => `form-control${errors[k] ? ' is-invalid' : ''}`;
  const id = isEdit ? 'modal-edit-user' : 'modal-add-user';
  return (
    <Modal
      id={id}
      title={isEdit ? 'Edit User' : 'Create New User Account'}
      icon={isEdit ? 'fa-user-pen' : 'fa-user-plus'}
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" form="form-user" className="btn btn-primary" id="btn-save-user" disabled={saving}>
            {isEdit ? 'Save Changes' : 'Create User'}
          </button>
        </>
      )}
    >
      {serverError && <div className="alert alert-danger" role="alert" id="user-form-error">{serverError}</div>}
      <form id="form-user" noValidate onSubmit={submit}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="u-name" className="form-label small fw-bold">Full Name</label>
            <input id="u-name" className={cls('name')} value={form.name} onChange={set('name')} placeholder="e.g. Roberto Gomez" required />
            <div className="invalid-feedback">{errors.name}</div>
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="u-email" className="form-label small fw-bold">Email Address (Login)</label>
            <input id="u-email" type="email" className={cls('email')} value={form.email} onChange={set('email')} disabled={isEdit} placeholder="e.g. roberto.gomez@chargeos.ph" required />
            <div className="invalid-feedback">{errors.email}</div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="u-role" className="form-label small fw-bold">System Role</label>
            <select id="u-role" className="form-select" value={form.role} onChange={set('role')}>
              {!ROLES.some((r) => r.value === form.role) && <option value={form.role}>{form.role}</option>}
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label htmlFor="u-phone" className="form-label small fw-bold">Phone Number</label>
            <input id="u-phone" className={cls('phone')} value={form.phone} onChange={set('phone')} placeholder="+63 917 555 0199" />
            <div className="invalid-feedback">{errors.phone}</div>
          </div>
        </div>
        <div className="row">
          {isEdit && (
            <div className="col-md-6 mb-3">
              <label htmlFor="u-status" className="form-label small fw-bold">Status</label>
              <select id="u-status" className="form-select" value={form.status} onChange={set('status')}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          )}
          <div className="col-md-6 mb-3">
            <label htmlFor="u-rfid" className="form-label small fw-bold">Physical RFID Card Tag (Optional)</label>
            <input id="u-rfid" className="form-control font-monospace" value={form.rfid} onChange={set('rfid')} placeholder="e.g. E200410668120248" />
          </div>
        </div>
      </form>
    </Modal>
  );
}

function PermissionsModal({ onClose, onSaved }) {
  const [matrix, setMatrix] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.adminPermissions().then((rows) => {
      const m = {};
      rows.forEach((r) => { m[`${r.role}:${r.capability}`] = r.allowed; });
      setMatrix(m);
    }).catch((e) => setError(e.message));
  }, []);

  const toggle = (key) => setMatrix((m) => ({ ...m, [key]: !m[key] }));
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const permissions = Object.entries(matrix).map(([key, allowed]) => {
        const [role, capability] = key.split(':');
        return { role, capability, allowed };
      });
      await api.adminSavePermissions(permissions);
      onSaved();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      id="modal-roles-matrix"
      title="Role-Based Access Control (RBAC) Permissions Matrix"
      icon="fa-shield-halved"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" id="btn-save-permissions" disabled={!matrix || saving} onClick={save}>Save Permissions</button>
        </>
      )}
    >
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {!matrix ? <div className="spinner-border text-primary" role="status" /> : (
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr className="text-muted small text-uppercase">
                <th>Capability Area</th>
                {ROLES.map((r) => <th key={r.value} className="text-center">{r.short}</th>)}
              </tr>
            </thead>
            <tbody className="small">
              {CAPABILITIES.map((c) => (
                <tr key={c.value}>
                  <td className="fw-bold text-dark">{c.label}</td>
                  {ROLES.map((r) => {
                    const key = `${r.value}:${c.value}`;
                    return (
                      <td key={r.value} className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`${r.label} – ${c.label}`}
                          data-perm={key}
                          checked={!!matrix[key]}
                          disabled={r.value === 'admin'}
                          onChange={() => toggle(key)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

/** Latest audited actions (who did what to which record). Hidden if the log cannot be loaded. */
function AuditLog({ refreshKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    api.adminAuditLog('?limit=50').then((list) => live && setRows(Array.isArray(list) ? list : [])).catch(() => live && setRows(null));
    return () => { live = false; };
  }, [refreshKey]);
  if (!rows) return null;
  return (
    <div className="card border shadow-sm mt-4" id="audit-log-card">
      <div className="card-header bg-white py-3">
        <h6 className="mb-0 fw-bold text-primary"><i className="fas fa-clipboard-list me-2" aria-hidden="true" />Audit log <span className="text-muted fw-normal small">(latest 50 actions)</span></h6>
      </div>
      <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
        <table className="table table-sm table-hover align-middle mb-0" id="audit-log-table">
          <thead className="table-light"><tr><th>Time</th><th>Actor</th><th>Action</th><th>Record</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-3">No audited actions yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="small text-nowrap">{new Date(r.created_at || r.createdAt).toLocaleString()}</td>
                <td className="small">{r.actor_email || r.actorEmail || 'system'}</td>
                <td className="small"><code>{r.action}</code></td>
                <td className="small">{r.entity_type || r.entityType}{(r.entity_id || r.entityId) ? ` · ${r.entity_id || r.entityId}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function UserRoleManagementPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [setPasswordLink, setSetPasswordLink] = useState(''); // demo: no invitation email is sent
  const [modal, setModal] = useState(null); // 'add' | 'perms' | { edit: user }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.adminListUsers({ q: q.trim(), role, status, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      setUsers(res.data);
      setTotal(res.meta?.total ?? res.data.length);
      setStats(await api.adminUserStats());
    } catch (e) {
      setError(e.message || 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }, [q, role, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  const createUser = async (f) => {
    const created = await api.adminCreateUser({ name: f.name.trim(), email: f.email.trim(), role: f.role, phone: f.phone.trim(), rfid: f.rfid.trim() });
    setSetPasswordLink(created?.demoSetPasswordLink || '');
    setModal(null);
    setNotice(`User account for ${f.name.trim()} created successfully.`);
    setPage(1);
    load();
  };

  const updateUser = async (u, f) => {
    await api.adminUpdateUser(u.id, {
      name: f.name.trim(),
      role: f.role,
      status: f.status,
      phone: f.phone.trim() || null,
      rfid: f.rfid.trim() || null,
    });
    setModal(null);
    setNotice(`${f.name.trim()} updated.`);
    load();
  };

  const toggleStatus = async (u) => {
    try {
      await api.adminUpdateUser(u.id, { status: u.is_active ? 'suspended' : 'active' });
      setNotice(`${u.name} ${u.is_active ? 'suspended' : 'reactivated'}.`);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const statCard = (label, value, badge, id, color = 'text-primary') => (
    <div className="col-12 col-md-3 mb-3">
      <div className="card border shadow-sm bg-white">
        <div className="card-body p-3">
          <div className="text-muted small text-uppercase fw-bold">{label}</div>
          <div className="d-flex justify-content-between align-items-baseline mt-2">
            <h3 className={`fw-bold mb-0 ${color}`} id={id}>{stats ? Number(value).toLocaleString() : '—'}</h3>
            <span className="badge bg-light text-dark border">{badge}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4" id="user-management-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1 text-primary">
            <i className="fas fa-users-cog me-2" aria-hidden="true" />User &amp; Role Management (Access Control)
          </h1>
          <p className="text-muted small mb-0">Administer system users, role-based access control (RBAC) permissions, and RFID keyfob credentials.</p>
        </div>
        <div className="mt-2 mt-md-0">
          <button type="button" className="btn btn-sm btn-outline-primary me-2" id="btn-open-matrix" onClick={() => setModal('perms')}>
            <i className="fas fa-shield-alt me-1" aria-hidden="true" /> RBAC Permissions Matrix
          </button>
          <button type="button" className="btn btn-sm btn-primary" id="btn-add-user" onClick={() => setModal('add')}>
            <i className="fas fa-user-plus me-1" aria-hidden="true" /> Create User Account
          </button>
        </div>
      </div>

      {notice && (
        <div className="alert alert-success alert-dismissible" role="status" id="urm-notice">
          {notice}
          {setPasswordLink && (
            <div className="small mt-2" id="urm-set-password-link">
              <i className="fa fa-flask me-1" aria-hidden="true" />Demo: no invitation email is sent. Give the new user this link to set a password: <CopyLink href={setPasswordLink} />
            </div>
          )}
          <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => { setNotice(''); setSetPasswordLink(''); }} />
        </div>
      )}
      {error && <div className="alert alert-danger" role="alert" id="urm-error">{error}</div>}

      <div className="row mb-2" id="metrics-banner">
        {statCard('Total System Accounts', stats?.total, 'Platform Roster', 'stat-user-count')}
        {statCard('Active Drivers', stats?.active_drivers, 'Drivers', 'stat-active-drivers', 'text-success')}
        {statCard('Operations Staff', stats?.operations_staff, 'NOC / Admin / Tech', 'stat-ops-staff', 'text-info')}
        {statCard('Suspended Accounts', stats?.suspended, 'Suspended', 'stat-suspended', 'text-danger')}
      </div>

      <div className="card border shadow-sm bg-white">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3">
          <h2 className="h6 mb-0 fw-bold text-primary"><i className="fas fa-list-ul me-2" aria-hidden="true" />User Accounts Directory</h2>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0">
            <div className="input-group input-group-sm urm-search">
              <span className="input-group-text bg-white"><i className="fas fa-search text-muted" aria-hidden="true" /></span>
              <input type="search" className="form-control" id="search-user" aria-label="Search users" placeholder="Search name, email, phone, RFID..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            </div>
            <select className="form-select form-select-sm urm-filter" id="filter-user-role" aria-label="Filter by role" value={role} onChange={(e) => { setRole(e.target.value); setPage(1); }}>
              <option value="">All Roles</option>
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select className="form-select form-select-sm urm-filter" id="filter-user-status" aria-label="Filter by status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0" id="users-table">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>Full Name</th><th>Email Address</th><th>System Role</th><th>Mobile Phone</th><th>Status</th><th>Last Login</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody id="users-table-body">
                {loading && users.length === 0 && (
                  <tr><td colSpan={7} className="text-center p-4"><div className="spinner-border spinner-border-sm text-primary" role="status" /></td></tr>
                )}
                {!loading && users.length === 0 && (
                  <tr><td colSpan={7} className="text-center p-4 text-muted">No user accounts match the search or filters.</td></tr>
                )}
                {users.map((u) => {
                  const rm = roleMeta(u.role);
                  return (
                    <tr key={u.id} data-email={u.email}>
                      <td className="fw-bold text-dark">{u.name}</td>
                      <td className="small text-muted font-monospace">{u.email}</td>
                      <td><span className={`badge ${rm.badge} px-2 py-1`}>{rm.label}</span></td>
                      <td className="small text-dark">{u.phone_number || '—'}</td>
                      <td><span className={`badge ${u.is_active ? 'bg-success' : 'bg-secondary'}`}>{u.is_active ? 'Active' : 'Suspended'}</span></td>
                      <td className="small text-muted">{fmtDate(u.last_login_at)}</td>
                      <td className="text-end text-nowrap">
                        <button type="button" className="btn btn-sm btn-outline-secondary me-1 btn-edit-user" title="Edit User" aria-label={`Edit ${u.name}`} onClick={() => setModal({ edit: u })}>
                          <i className="fas fa-edit" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger btn-toggle-status" title={u.is_active ? 'Suspend' : 'Reactivate'} aria-label={`${u.is_active ? 'Suspend' : 'Reactivate'} ${u.name}`} onClick={() => toggleStatus(u)}>
                          <i className={`fas ${u.is_active ? 'fa-user-slash' : 'fa-user-check'}`} aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
          <span className="text-muted small">Showing <strong id="users-range-start">{start}</strong>–<strong id="users-range-end">{end}</strong> of <strong id="users-count">{total}</strong> users</span>
          <nav aria-label="Users pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item${page === 1 ? ' disabled' : ''}`}><button type="button" className="page-link" aria-label="Previous page" onClick={() => setPage((p) => p - 1)}>&laquo;</button></li>
              <li className="page-item active"><span className="page-link">{page} / {totalPages}</span></li>
              <li className={`page-item${page >= totalPages ? ' disabled' : ''}`}><button type="button" className="page-link" aria-label="Next page" onClick={() => setPage((p) => p + 1)}>&raquo;</button></li>
            </ul>
          </nav>
        </div>
      </div>

      <AuditLog refreshKey={notice} />

      {modal === 'add' && <UserForm onSubmit={createUser} onCancel={() => setModal(null)} />}
      {modal?.edit && <UserForm isEdit initial={modal.edit} onSubmit={(f) => updateUser(modal.edit, f)} onCancel={() => setModal(null)} />}
      {modal === 'perms' && (
        <PermissionsModal onClose={() => setModal(null)} onSaved={() => { setModal(null); setNotice('Role permissions saved.'); }} />
      )}
    </div>
  );
}
